import { useEffect, useRef, useCallback, useState } from "react";

import {
  markMessageDeletedForEveryone,
  markMessageDeletedForMe,  saveMessage
} from "../db/fsDB";


const resolveMsgId = (m) =>
  m?.id ?? m?.messageId ?? m?.raw?.key?.msgId;

export function useChatWindowEffects({
  /* identity */
  myChatId,
  socket,
  cryptoFns,
activeTab,
  TABS,
  mobileView,
  isActive,
  /* selection */
  selectedItem,
  setSelectedItem,

  /* messages */
  setMessages,
  getUserKey,
  getGroupKey,

  /* group members */
  setGroupMembers,

  /* users list */
  setUsers,
  users,

  /* groups list */
  setGroups,
  groups,
  liveGroup,

  /* invite link */
  showGroupMembers,
  setGroupInviteLink,

  /* emoji */
  setRecentEmojis,

  /* reply auto-focus */
  selectedReply,
  inputRef,

  /* toast */
  showToast,

  /* 📌 pinned messages state — managed in ChatWindow, updated here */
  setPinnedMessages,
}) {
  const lastSelectedRef = useRef(null);
  const unreadDividerRef = useRef({}); // chatKey/groupKey -> { msgId, count } snapshot, captured before the seen-flip

  /* =====================================================
     🔥 CHAT SELECTION ON SELECTED ITEM CHANGE
  ===================================================== */
const isViewingChats = !mobileView || activeTab === TABS?.CHATS;
// After
useEffect(() => {
  if (!myChatId || !socket) return;

  // Chat List is visible. Keep ChatWindow alive and do not
  // leave or reset the current conversation.
  if (!selectedItem) return;

  // Mobile never unmounts ChatWindow on tab switch the way desktop
  // does — without this check, a chat merely "selected" in the
  // background gets treated as "being viewed": its messages get
  // marked seen and its badge gets zeroed while the user is on Feed.
  if (!isViewingChats) return;

  // Desktop AND mobile both keep every opened chat's ChatWindow
  // permanently mounted with a fixed selectedItem (see chatInstances
  // in DesktopChatLayout/MobileChatPanel) — only isActive toggles when
  // switching between already-open chats. Without this guard, this
  // effect only ever fires once, on that instance's first mount, and
  // never re-marks messages seen / re-zeroes the badge on reactivation.
  if (!isActive) return;
  if (selectedItem.type === "user") {
    const chatKey = getUserKey(selectedItem.chatId);

    if (lastSelectedRef.current !== chatKey) {
      // Leave the previous conversation only when switching
      // directly between conversations.
      if (lastSelectedRef.current) {
        socket.emit("leave-chat");
      }

      lastSelectedRef.current = chatKey;

      socket.emit("select-chat", {
        recipientChatId: selectedItem.chatId,
      });
    }

    // Tell the server these messages were actually read — without this,
    // the next unread-count/unread-reset broadcast just overwrites our
    // optimistic local reset below with the server's stale count.
    socket.emit("mark-chat-read", {
      recipientChatId: selectedItem.chatId,
    });

    setMessages((prev) => {
      const list = prev[chatKey] || [];
      const unseenList = list.filter((m) => !m.fromMe && !m.seen);
      const firstUnseen = unseenList[0];
      const needsUpdate = !!firstUnseen;

      if (!needsUpdate) return prev;

      // Remember where the unread run started AND how many were unread,
      // captured in the same tick as the flip below — this can never lose
      // the race with MessageList's own render, unlike re-deriving it later.
      if (!unreadDividerRef.current[chatKey]) {
        unreadDividerRef.current[chatKey] = {
          msgId: resolveMsgId(firstUnseen),
          count: unseenList.length,
        };
      }

      const updated = list.map((m) =>
        !m.fromMe && !m.seen
          ? { ...m, seen: true, seenAt: Date.now() }
          : m
      );

      Promise.all(
        updated
          .filter((m, i) => m !== list[i])
          .map((m) => saveMessage(m))
      ).catch(() => {});
      return { ...prev, [chatKey]: updated };
    });

    setUsers((prev) =>
      prev.map((u) =>
        String(u.chatId) === String(selectedItem.chatId)
          ? { ...u, unreadCount: 0 }
          : u
      )
    );
  }

  if (selectedItem.type === "group") {
    const groupKey = getGroupKey(selectedItem.groupId);

    if (lastSelectedRef.current !== groupKey) {
      if (lastSelectedRef.current) {
        socket.emit("leave-chat");
      }

      lastSelectedRef.current = groupKey;

      socket.emit("select-group", {
        groupId: selectedItem.groupId,
      });
    }

    // Same read-receipt problem as the DM branch above — the server
    // needs an explicit signal or it keeps re-broadcasting stale counts.
    socket.emit("mark-group-read", {
      groupId: selectedItem.groupId,
    });

    setMessages((prev) => {
      const list = prev[groupKey] || [];
      const unseenList = list.filter((m) => !m.fromMe && !m.seen);
      const firstUnseen = unseenList[0];
      const needsUpdate = !!firstUnseen;

      if (!needsUpdate) return prev;

      if (!unreadDividerRef.current[groupKey]) {
        unreadDividerRef.current[groupKey] = {
          msgId: resolveMsgId(firstUnseen),
          count: unseenList.length,
        };
      }

      const updated = list.map((m) =>
        !m.fromMe && !m.seen
          ? { ...m, seen: true, seenAt: Date.now() }
          : m
      );

      Promise.all(
        updated
          .filter((m, i) => m !== list[i])
          .map((m) => saveMessage(m))
      ).catch(() => {});

      return { ...prev, [groupKey]: updated };
    });

    setGroups((prev) =>
      prev.map((g) =>
        String(g.groupId) === String(selectedItem.groupId)
          ? { ...g, unreadCount: 0 }
          : g
      )
    );
  }
}, [
  selectedItem?.type,
  selectedItem?.chatId,
  selectedItem?.groupId,
  myChatId,
  socket,
  getUserKey,
  getGroupKey,
  setMessages,
  setUsers,
  setGroups,
  isViewingChats,
    isActive,
]);

  /* =====================================================
     🔄 RE-ANNOUNCE OPEN CHAT ON SOCKET RECONNECT
     The server drops openChatId/openGroupId for this device
     while offline. Without this, a message that arrives during
     the gap gets counted as unread even though this chat is
     still open, and the badge "re-fires" on reconnect.
  ===================================================== */
  useEffect(() => {
    if (!socket) return;
    const onReconnect = () => {
      if (!selectedItem || !isViewingChats || !isActive) return;
      if (selectedItem.type === "user") {
        socket.emit("select-chat", { recipientChatId: selectedItem.chatId });
      } else if (selectedItem.type === "group") {
        socket.emit("select-group", { groupId: selectedItem.groupId });
      }
    };
    socket.on("connect", onReconnect);
    return () => socket.off("connect", onReconnect);
  }, [socket, selectedItem, isViewingChats, isActive]);

  /* =====================================================
     🚪 LEAVE CHAT WHEN THE MOBILE TAB SWITCHES AWAY
     Desktop doesn't need this — ChatWindow unmounts instead,
     which already triggers the effect below. On mobile it stays
     mounted, so we have to tell the server explicitly, and clear
     lastSelectedRef so returning to Chats re-runs select/seen/reset.
  ===================================================== */
  useEffect(() => {
    if (!mobileView || isViewingChats) return;
    if (!lastSelectedRef.current) return;

    socket?.emit("leave-chat");
    delete unreadDividerRef.current[lastSelectedRef.current];
    lastSelectedRef.current = null;
  }, [isViewingChats, mobileView, socket]);
  /* =====================================================
     🔌 LEAVE CHAT ON UNMOUNT
  ===================================================== */
 useEffect(() => {
  return () => {
    // Real component unmount only.
    delete unreadDividerRef.current[lastSelectedRef.current];
    lastSelectedRef.current = null;
    socket?.emit("leave-chat");
  };
}, [socket]);

  /* =====================================================
     🔤 EMOJI PERSISTENCE
     (Scrolling is now handled by useChatScroll)
  ===================================================== */
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("recent-emojis") || "[]");
      if (Array.isArray(stored)) setRecentEmojis(stored);
    } catch (_e) {}
  }, []);

  /* =====================================================
     🔗 REQUEST GROUP INVITE LINK WHEN PANEL OPENS
  ===================================================== */
  useEffect(() => {
    if (!socket || !showGroupMembers || !selectedItem) return;
    if (selectedItem.type !== "group" || !selectedItem.groupId) return;
    socket.emit("group-request-invite-link", {
      groupId: selectedItem.groupId,
      chatId:  myChatId,
    });
  }, [socket, showGroupMembers, selectedItem, myChatId]);

  
 useEffect(() => {
  if (!socket) return;

  const handler = ({ peerId, msgId, pinned, expiresAt }) => {
    const chatKey = getUserKey(peerId);

    setMessages((prev) => {
      const list = prev[chatKey] || [];

      return {
        ...prev,
        [chatKey]: list.map((m) => {
          const id = m?.id || m?.messageId || m?.raw?.key?.msgId;
          if (id !== msgId) return m;

          const current = Array.isArray(m.pinnedBy) ? m.pinnedBy : [];

          let updatedMsg;

          if (pinned) {
            updatedMsg = {
              ...m,
              pinnedBy: [...new Set([...current, myChatId])],
              pinnedAt: Date.now(),
              expiresAt,
            };
          } else {
            const updated = current.filter((id) => id !== myChatId);

            updatedMsg = {
              ...m,
              pinnedBy: updated,
              ...(updated.length === 0
                ? { pinnedAt: null, expiresAt: null }
                : {}),
            };
          }

          // 🔥 persist to IDB
      saveMessage(updatedMsg).catch(() => {});

          return updatedMsg;
        }),
      };
    });
  };

  socket.on("dm-message-pinned", handler);
  return () => socket.off("dm-message-pinned", handler);
}, [socket, getUserKey, setMessages, myChatId]);

useEffect(() => {
  if (!socket) return;

  const handler = ({ groupId, msgId, pinned, pinnedBy, expiresAt }) => {
    const chatKey = getGroupKey(groupId);

    setMessages((prev) => {
      const list = prev[chatKey] || [];

      return {
        ...prev,
        [chatKey]: list.map((m) => {
          const id = m?.id || m?.messageId || m?.raw?.key?.msgId;
          if (id !== msgId) return m;

          let updatedMsg;

          if (pinned) {
            updatedMsg = {
              ...m,
              pinnedBy,
              pinnedAt: Date.now(),
              expiresAt,
            };
          } else {
            updatedMsg = {
              ...m,
              pinnedBy: null,
              pinnedAt: null,
              expiresAt: null,
            };
          }

          // 🔥 persist to IDB
        saveMessage(updatedMsg).catch(() => {});

          return updatedMsg;
        }),
      };
    });
  };

  socket.on("group-message-pinned", handler);
  return () => socket.off("group-message-pinned", handler);
}, [socket, getGroupKey, setMessages]);

  /* =====================================================
     📡 SOCKET: group-avatar-updated
  ===================================================== */
  useEffect(() => {
    if (!socket) return;
    const handler = ({ groupId, groupImageUrl }) => {
      setSelectedItem((prev) =>
        prev?.type === "group" && prev.groupId === groupId
          ? { ...prev, groupImageUrl }
          : prev
      );
      setGroups((prev) =>
        prev.map((g) => (g.groupId === groupId ? { ...g, groupImageUrl } : g))
      );
    };
    socket.on("group-avatar-updated", handler);
    return () => socket.off("group-avatar-updated", handler);
  }, [socket, setSelectedItem, setGroups]);

  /* =====================================================
     📡 SOCKET: group-admin-promoted / demoted
  ===================================================== */
  useEffect(() => {
    if (!socket) return;

  const onPromoted = ({ groupId, targetChatId, actorChatId }) => {
  if (selectedItem?.type !== "group" || selectedItem.groupId !== groupId) return;

  // Only the promoted member sees a toast (actor already got one from their own UI)
  if (String(myChatId) === String(targetChatId)) {
    showToast("You were promoted to admin");
  }

  setGroupMembers((prev) => ({
    ...prev,
    [groupId]: (prev[groupId] || []).map((m) =>
      String(m.chatId) === String(targetChatId) ? { ...m, isAdmin: true } : m
    ),
  }));
};

const onDemoted = ({ groupId, targetChatId, actorChatId }) => {
  if (selectedItem?.type !== "group" || selectedItem.groupId !== groupId) return;

  // Only the demoted member sees a toast
  if (String(myChatId) === String(targetChatId)) {
    showToast("You were removed as admin");
  }

  setGroupMembers((prev) => ({
    ...prev,
    [groupId]: (prev[groupId] || []).map((m) =>
      String(m.chatId) === String(targetChatId) ? { ...m, isAdmin: false } : m
    ),
  }));
};

    socket.on("group-admin-promoted", onPromoted);
    socket.on("group-admin-demoted", onDemoted);
    return () => {
      socket.off("group-admin-promoted", onPromoted);
      socket.off("group-admin-demoted", onDemoted);
    };
  }, [socket, selectedItem, setGroupMembers, showToast]);

  /* =====================================================
     📡 SOCKET: user-avatar-updated
  ===================================================== */
  useEffect(() => {
    if (!socket) return;
    const handler = ({ chatId, profileImageUrl }) => {
      setUsers((prev) =>
        prev.map((u) =>
          String(u.chatId) === String(chatId) ? { ...u, profileImageUrl } : u
        )
      );
      setSelectedItem((prev) =>
        prev?.type === "user" && String(prev.chatId) === String(chatId)
          ? { ...prev, profileImageUrl }
          : prev
      );
    };
    socket.on("user-avatar-updated", handler);
    return () => socket.off("user-avatar-updated", handler);
  }, [socket, setUsers, setSelectedItem]);

  /* =====================================================
     📡 SOCKET: group-invite-link
  ===================================================== */
  useEffect(() => {
    if (!socket) return;
    const handler = ({ groupId, inviteLink }) => {
      if (selectedItem?.type === "group" && selectedItem.groupId === groupId) {
        setGroupInviteLink(inviteLink);
      }
    };
    socket.on("group-invite-link", handler);
    return () => socket.off("group-invite-link", handler);
  }, [socket, selectedItem]);
  /* =====================================================
     🔄 SYNC selectedItem METADATA FROM liveGroup
  ===================================================== */
  useEffect(() => {
    if (selectedItem?.type === "group" && liveGroup) {
      const groupId = selectedItem.groupId;
      setSelectedItem((prev) => {
        // Bail out if the selection changed (or was cleared) since this
        // effect was scheduled — never resurrect a group object out of null.
        if (prev?.type !== "group" || prev.groupId !== groupId) return prev;

        if (
          prev.name          === liveGroup.name &&
          prev.groupImageUrl === liveGroup.groupImageUrl
        )
          return prev;

        return {
          ...prev,
          name:          liveGroup.name,
          groupImageUrl: liveGroup.groupImageUrl,
        };
      });
    }
  }, [liveGroup, selectedItem?.type, selectedItem?.groupId, setSelectedItem]);
 
  /* =====================================================
     🎯 AUTO-FOCUS INPUT AFTER REPLY SELECTED
  ===================================================== */
  useEffect(() => {
    if (!selectedReply) return;
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [selectedReply]);

  return { unreadDividerRef };
}